// Amendment history inside the appointment editor: the append-only audit rows
// recorded by the admin :update/:cancel endpoints (who, when, why, and the
// field-level before → after diffs). Follows AppointmentPatientPanel's
// collapsible-section pattern: lazy fetch keyed on the sheet opening, count
// badge in the header, skeletons while loading.

import { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { ChevronDown, History } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fetchAppointmentHistory } from "@/lib/api/admin";
import { FieldLabel } from "./form-fields";

const FIELD_LABELS = {
  title: "Title",
  notes: "Notes",
  start: "Start",
  end: "End",
  timezone: "Timezone",
  doctor: "Doctor",
  clinic: "Clinic",
  status: "Status",
  color: "Color",
  lock15m: "15-min lock",
};

const ACTION_LABELS = {
  updated: "Updated",
  cancelled: "Cancelled",
  restored: "Restored",
};

function formatHistoryValue(field, value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  if (field === "start" || field === "end") {
    const parsed = DateTime.fromISO(raw, { setZone: true });
    if (parsed.isValid) return parsed.toFormat("d LLL yyyy, HH:mm");
  }
  if (field === "lock15m") {
    return raw === "true" ? "on" : "off";
  }
  return raw;
}

function formatHistoryAt(value) {
  const parsed = DateTime.fromISO(String(value || ""));
  if (!parsed.isValid) return "";
  return parsed.toLocal().toFormat("ccc d LLL yyyy, HH:mm");
}

function HistoryRow({ entry }) {
  const actionLabel = ACTION_LABELS[entry.action] || entry.action || "Updated";
  const tone =
    entry.action === "cancelled"
      ? "text-destructive"
      : entry.action === "restored"
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-foreground";
  return (
    <li className="space-y-1.5 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
        <span className={cn("font-semibold", tone)}>{actionLabel}</span>
        <span className="text-muted-foreground">{formatHistoryAt(entry.at)}</span>
        {entry.actorName ? (
          <span className="text-muted-foreground">
            by <span className="font-medium text-foreground/90">{entry.actorName}</span>
          </span>
        ) : null}
      </div>
      {entry.changes?.length ? (
        <ul className="space-y-0.5">
          {entry.changes.map((change, index) => (
            <li
              key={`${entry.id}-${change.field}-${index}`}
              className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-xs text-muted-foreground"
            >
              <span className="shrink-0 font-medium text-foreground/80">
                {FIELD_LABELS[change.field] || change.field}:
              </span>
              <span className="min-w-0 break-words">
                {formatHistoryValue(change.field, change.from)}
                <span className="mx-1 text-muted-foreground/60" aria-hidden>
                  →
                </span>
                {formatHistoryValue(change.field, change.to)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {entry.reason ? (
        <p className="rounded-md bg-muted/40 px-2 py-1 text-xs italic text-foreground/85">
          “{entry.reason}”
        </p>
      ) : null}
    </li>
  );
}

/**
 * Collapsible "History" section for the appointment editor. Fetches on sheet
 * open; hides itself entirely while empty (most appointments have no
 * amendments, and an empty audit section is just noise).
 */
export function AppointmentHistoryPanel({ event, open }) {
  const appointmentId = String(event?.id || "").trim();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open || !appointmentId) {
      setItems([]);
      setError("");
      setExpanded(false);
      return undefined;
    }
    let cancelled = false;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError("");
    fetchAppointmentHistory({ appointmentId })
      .then((res) => {
        if (cancelled || requestRef.current !== requestId) return;
        setItems(res.items);
      })
      .catch((err) => {
        if (!cancelled && err?.status !== 404) {
          setError(err?.message || "Could not load history.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, appointmentId]);

  if (!appointmentId) return null;
  // Appear only once there is something to show — most appointments have no
  // amendments, and an empty audit section is just noise.
  if (!error && items.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="overflow-hidden rounded-lg border border-border/70">
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          className="flex w-full items-center gap-2 bg-muted/20 px-3 py-2 text-left transition-colors hover:bg-muted/40"
        >
          <History className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <FieldLabel>History</FieldLabel>
          {items.length ? (
            <Badge variant="secondary" className="h-5 px-1.5 text-[0.625rem]">
              {items.length}
            </Badge>
          ) : null}
          <ChevronDown
            className={cn(
              "ml-auto size-4 shrink-0 text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
            aria-hidden
          />
        </button>
        {expanded ? (
          loading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-3 w-48" />
              <Skeleton className="h-3 w-56" />
            </div>
          ) : error ? (
            <p className="p-3 text-xs text-destructive">{error}</p>
          ) : (
            <ScrollArea viewportClassName="max-h-72">
              <ul className="divide-y divide-border/60">
                {items.map((entry, index) => (
                  <HistoryRow key={entry.id || index} entry={entry} />
                ))}
              </ul>
            </ScrollArea>
          )
        ) : null}
      </div>
    </div>
  );
}
